import { ThemeColor, ThemeTonalRange } from '@philips-emr/tws-constants';
import { getViewportElement } from '@philips-emr/tws-utils';

const COLOR_MAP = new Map<string, ThemeColor>([
  ['aqua', ThemeColor.AQUA],
  ['blue', ThemeColor.BLUE],
  ['green', ThemeColor.GREEN],
  ['grey', ThemeColor.GREY],
  ['group-blue', ThemeColor.GROUP_BLUE],
  ['orange', ThemeColor.ORANGE],
  ['pink', ThemeColor.PINK],
  ['purple', ThemeColor.PURPLE],
]);

const TONAL_RANGE_MAP = new Map<string, ThemeTonalRange>([
  ['bright', ThemeTonalRange.BRIGHT],
  ['ultra-light', ThemeTonalRange.ULTRA_LIGHT],
  ['very-dark', ThemeTonalRange.VERY_DARK],
  ['very-light', ThemeTonalRange.VERY_LIGHT],
]);

type AquaAccents = ThemeColor.BLUE | ThemeColor.GREEN | ThemeColor.GROUP_BLUE | ThemeColor.ORANGE | ThemeColor.PINK | ThemeColor.PURPLE;
type BlueAccents = ThemeColor.AQUA | ThemeColor.GREEN | ThemeColor.ORANGE | ThemeColor.PINK | ThemeColor.PURPLE;
type GreenAccents = ThemeColor.AQUA | ThemeColor.BLUE | ThemeColor.GROUP_BLUE | ThemeColor.ORANGE | ThemeColor.PINK | ThemeColor.PURPLE;
type GreyAccents = ThemeColor.AQUA | ThemeColor.BLUE | ThemeColor.GREEN | ThemeColor.GROUP_BLUE | ThemeColor.ORANGE | ThemeColor.PINK | ThemeColor.PURPLE;
type GroupBlueAccents = ThemeColor.AQUA | ThemeColor.GREEN | ThemeColor.ORANGE | ThemeColor.PINK | ThemeColor.PURPLE;
type OrangeAccents = ThemeColor.AQUA | ThemeColor.BLUE | ThemeColor.GREEN | ThemeColor.GROUP_BLUE | ThemeColor.PINK | ThemeColor.PURPLE;
type PinkAccents = ThemeColor.AQUA | ThemeColor.BLUE | ThemeColor.GREEN | ThemeColor.GROUP_BLUE | ThemeColor.ORANGE | ThemeColor.PURPLE;
type PurpleAccents = ThemeColor.AQUA | ThemeColor.BLUE | ThemeColor.GREEN | ThemeColor.GROUP_BLUE | ThemeColor.ORANGE | ThemeColor.PINK;

type SupportedAccents<T extends ThemeColor> =
  T extends ThemeColor.AQUA ? AquaAccents :
    T extends ThemeColor.BLUE ? BlueAccents :
      T extends ThemeColor.GREEN ? GreenAccents :
        T extends ThemeColor.GREY ? GreyAccents :
          T extends ThemeColor.GROUP_BLUE ? GroupBlueAccents :
            T extends ThemeColor.ORANGE ? OrangeAccents :
              T extends ThemeColor.PINK ? PinkAccents :
                T extends ThemeColor.PURPLE ? PurpleAccents :
                  never;

type SupportedAccentsList = {
  [key in ThemeColor]: SupportedAccents<key>[];
};

const supportedAccents: SupportedAccentsList = {
  [ThemeColor.AQUA]: [ThemeColor.BLUE, ThemeColor.GREEN, ThemeColor.GROUP_BLUE, ThemeColor.ORANGE, ThemeColor.PINK, ThemeColor.PURPLE],
  [ThemeColor.BLUE]: [ThemeColor.AQUA, ThemeColor.GREEN, ThemeColor.ORANGE, ThemeColor.PINK, ThemeColor.PURPLE],
  [ThemeColor.GREEN]: [ThemeColor.AQUA, ThemeColor.BLUE, ThemeColor.GROUP_BLUE, ThemeColor.ORANGE, ThemeColor.PINK, ThemeColor.PURPLE],
  [ThemeColor.GREY]: [ThemeColor.AQUA, ThemeColor.BLUE, ThemeColor.GREEN, ThemeColor.GROUP_BLUE, ThemeColor.ORANGE, ThemeColor.PINK, ThemeColor.PURPLE],
  [ThemeColor.GROUP_BLUE]: [ThemeColor.AQUA, ThemeColor.GREEN, ThemeColor.ORANGE, ThemeColor.PINK, ThemeColor.PURPLE],
  [ThemeColor.ORANGE]: [ThemeColor.AQUA, ThemeColor.BLUE, ThemeColor.GREEN, ThemeColor.GROUP_BLUE, ThemeColor.PINK, ThemeColor.PURPLE],
  [ThemeColor.PINK]: [ThemeColor.AQUA, ThemeColor.BLUE, ThemeColor.GREEN, ThemeColor.GROUP_BLUE, ThemeColor.ORANGE, ThemeColor.PURPLE],
  [ThemeColor.PURPLE]: [ThemeColor.AQUA, ThemeColor.BLUE, ThemeColor.GREEN, ThemeColor.GROUP_BLUE, ThemeColor.ORANGE, ThemeColor.PINK],
};

export interface Theme {
  accentColor?: ThemeColor;
  color?: ThemeColor;
  contentTonalRange?: ThemeTonalRange;
  navigationTonalRange?: ThemeTonalRange;
}

export interface Options {
  accentColor?: boolean;
  color?: boolean;
  content?: boolean;
  navigation?: boolean;
}

export interface ThemeService {
  getColorFromString: (color: string) => ThemeColor | undefined;
  getSupportedAccents: (color: ThemeColor) => ThemeColor[];
  getTheme: () => Theme;
  getTonalRangeFromSring: (tonalRange: string) => ThemeTonalRange | undefined;
  removeTheme: (options: Options) => void;
  setColors: <T extends ThemeColor>(color: T, accentColor: SupportedAccents<T>) => void;
  setContentTonalRange: (tonalRange: ThemeTonalRange) => void;
  setNavigationTonalRange: (tonalRange: ThemeTonalRange) => void;
}

const accentColorRegex = /t-color-accent-[a-z-]*/;
const colorRegex = /t-color-[a-z-]*/;
const contentTonalRangeRegex = /t-content-[a-z-]*/;
const navigationTonalRangeRegex = /t-nav-[a-z-]*/;

function createThemeService(): ThemeService {
  const viewport = getViewportElement(true);

  const getColorFromString = (color: string): ThemeColor | undefined => {
    return COLOR_MAP.get(color);
  };

  const getTonalRangeFromSring = (tonalRange: string): ThemeTonalRange | undefined => {
    return TONAL_RANGE_MAP.get(tonalRange);
  };

  const getSupportedAccents = (color: ThemeColor): ThemeColor[] => {
    return supportedAccents[color];
  };

  const getTheme = (): Theme => {
    const className = viewport.className;

    const [accentColorStr] = className.match(accentColorRegex) ?? [];
    const [colorStr] = className.match(colorRegex) ?? [];
    const [contentTonalRangeStr] = className.match(contentTonalRangeRegex) ?? [];
    const [navigationTonalRangeStr] = className.match(navigationTonalRangeRegex) ?? [];

    return {
      accentColor: accentColorStr ? getColorFromString(accentColorStr.replace('t-color-accent-', '')) : undefined,
      color: colorStr ? getColorFromString(colorStr.replace('t-color-', '')) : undefined,
      contentTonalRange: contentTonalRangeStr ? getTonalRangeFromSring(contentTonalRangeStr.replace('t-content-', '')) : undefined,
      navigationTonalRange: navigationTonalRangeStr ? getTonalRangeFromSring(navigationTonalRangeStr.replace('t-nav-', '')) : undefined,
    };
  };

  const removeTheme = (options: Options): void => {
    const { accentColor = false, color = false, content = false, navigation = false } = options;
    let className = viewport.className;

    if (accentColor) {
      className = className.replace(accentColorRegex, '');
    }

    if (color) {
      className = className.replace(colorRegex, '');
    }

    if (content) {
      className = className.replace(contentTonalRangeRegex, '');
    }

    if (navigation) {
      className = className.replace(navigationTonalRangeRegex, '');
    }

    viewport.className = className.trim();
  };

  const setColors = <T extends ThemeColor>(color: T, accentColor: SupportedAccents<T>): void => {
    const supportedAccents = getSupportedAccents(color);

    if (!supportedAccents.includes(accentColor)) {
      throw Error(`[Theme Service]: The color ${color} does not support the color accent ${accentColor}`);
    }

    const colorClass = `t-color-${color}`;
    const accentColorClass = `t-color-accent-${accentColor}`;

    removeTheme({ accentColor: true, color: true });

    viewport.classList.add(colorClass, accentColorClass);
  };

  const setNavigationTonalRange = (tonalRange: ThemeTonalRange): void => {
    const navigationClass = `t-nav-${tonalRange}`;

    removeTheme({ navigation: true });

    viewport.classList.add(navigationClass);
  };

  const setContentTonalRange = (tonalRange: ThemeTonalRange): void => {
    const contentClass = `t-content-${tonalRange}`;

    removeTheme({ content: true });

    viewport.classList.add(contentClass);
  };

  return {
    getColorFromString,
    getSupportedAccents,
    getTheme,
    getTonalRangeFromSring,
    removeTheme,
    setColors,
    setContentTonalRange,
    setNavigationTonalRange,
  };
}

const themeService = createThemeService();

export default themeService;
